import type {
  ChainType,
  ChainVariantToken,
  Token,
  TokenKeyWithMetadata,
} from '@polaris/core/models'
import { apiClient } from './client'

interface GetTokensSearchParams {
  page: string
  'per-page': string
  query?: string
  'chain-id'?: string
  'with-price'?: string
}

interface TokenSearchResponse {
  assets: ChainVariantToken[]
  page: number
  /** All chains from search across all pages. */
  chain_ids: string[]
}

/** Query for searching for tokens, grouped by variants across chains.
 *  Supports:
 *  * Pagination
 *  * Filtering by chain ID
 *  * Searching by query string
 *  * Whether to include price in the response */
export const getSearchTokens = (
  searchParams?: Partial<GetTokensSearchParams>,
): Promise<TokenSearchResponse> =>
  apiClient
    .get('search/chain-variant-assets', {
      searchParams: JSON.parse(JSON.stringify(searchParams)),
      // Server side caching -- since we hydrate the first page of tokens on the client from the server
      next: {
        // https://nextjs.org/docs/app/building-your-application/caching
        // https://nextjs.org/docs/app/api-reference/functions/fetch#optionsnextrevalidate
        revalidate: 60 * 40, // 40 minutes
      },
    })
    .json<TokenSearchResponse>()

interface GetTokenAcrossChainsRequest {
  denom: string
  chain: string
}

export interface TokenAcrossChains {
  requested_asset: Token
  asset_across_chains: TokenKeyWithMetadata[]
}

export const getTokenAcrossChains = (
  params: GetTokenAcrossChainsRequest,
): Promise<TokenAcrossChains> =>
  apiClient
    .get('search/asset-across-chains', {
      searchParams: {
        chain: params.chain,
        denom: params.denom,
        'with-price': 'true',
      },
      // Server side caching
      next: {
        // https://nextjs.org/docs/app/building-your-application/caching
        // https://nextjs.org/docs/app/api-reference/functions/fetch#optionsnextrevalidate
        revalidate: 60, // 1 minute
      },
    })
    .json<TokenAcrossChains>()

export interface TrendingAsset {
  denom: string
  kind: string
  kind_logo_img_url: string
  kind_display_name: string
  display_name: string
  /**
   * Precision means that one 1 display unit = 10^{precision} base units
   */
  precision: number
  logo_img_url: string
  price_usd: string
  ticker: string
  chain_id: string
  chain_name: string
  chain_type: ChainType
  chain_img: string
  market_cap_rank: number
  market_cap: string
  total_volume: string
  price_change_percentage_24h: number
  last_updated: string
}

export const getTrendingTokens = (): Promise<TrendingAsset[]> =>
  apiClient
    .get('trending-assets', {
      // Server side caching
      // Note: disabled in dev mode to prevent excessive memory usage
      next: {
        // https://nextjs.org/docs/app/building-your-application/caching
        // https://nextjs.org/docs/app/api-reference/functions/fetch#optionsnextrevalidate
        // Aligned with backend cache: 5min (app/registryprovider/registryprovider.go:116 - assetPairsRefreshInterval)
        revalidate: 60 * 5, // 5 minutes
        tags: ['tokens', 'trending'],
      },
    })
    .json<TrendingAsset[]>()
